/**
 * 机械价格配置API服务
 */

// 生产环境使用相对路径，开发环境使用localhost
// Use relative path in production, localhost in development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export interface EquipmentPrice {
  _id: string;
  equipmentName: string;
  model?: string;
  unit: string;
  price?: number;
  shiftPrice?: number;
  category?: string;
  specifications?: string;
  effectiveDate: string;
  createdAt: string;
  updatedAt: string;
}

export type EquipmentPriceType = 'hour' | 'shift';

export interface EquipmentPriceResolvedRate {
  type: EquipmentPriceType;
  label: string;
  priceTypeLabel: string;
  price: number;
  hourlyPrice: number;
}

export interface EquipmentPriceCreateData {
  equipmentName: string;
  model?: string;
  unit: string;
  price?: number;
  shiftPrice?: number;
  category?: string;
  specifications?: string;
  effectiveDate?: string;
}

export interface EquipmentPriceUpdateData {
  equipmentName?: string;
  model?: string;
  unit?: string;
  price?: number;
  shiftPrice?: number;
  category?: string;
  specifications?: string;
  effectiveDate?: string;
}

export interface EquipmentPriceListParams {
  page?: number;
  limit?: number;
  equipmentName?: string;
  model?: string;
  category?: string;
  unit?: string;
}

export interface EquipmentPriceListResponse {
  success: boolean;
  data: EquipmentPrice[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  errors?: any[];
}

class EquipmentPriceService {
  private normalizeEquipmentPrice(item: any): EquipmentPrice {
    return {
      ...item,
      model: item?.model ?? item?.equipmentModel,
    };
  }

  resolveUsageRate(item?: Partial<EquipmentPrice> | null): EquipmentPriceResolvedRate | null {
    if (!item) {
      return null;
    }

    const shiftPrice = typeof item.shiftPrice === 'number' ? item.shiftPrice : Number(item.shiftPrice);
    if (Number.isFinite(shiftPrice) && shiftPrice > 0) {
      return {
        type: 'shift',
        label: '台班',
        priceTypeLabel: '台班单价',
        price: shiftPrice,
        hourlyPrice: Math.round((shiftPrice / 8) * 100) / 100,
      };
    }

    const hourPrice = typeof item.price === 'number' ? item.price : Number(item.price);
    if (Number.isFinite(hourPrice) && hourPrice > 0) {
      return {
        type: 'hour',
        label: '小时',
        priceTypeLabel: '小时单价',
        price: hourPrice,
        hourlyPrice: hourPrice,
      };
    }

    return null;
  }

  private normalizeEquipmentPriceResponse<T extends ApiResponse<any>>(response: T): T {
    if (!response?.data) {
      return response;
    }

    return {
      ...response,
      data: this.normalizeEquipmentPrice(response.data),
    };
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`API请求失败 [${endpoint}]:`, error);
      throw error;
    }
  }

  /**
   * 获取机械价格列表
   */
  async getEquipmentPrices(params: EquipmentPriceListParams = {}): Promise<EquipmentPriceListResponse> {
    const searchParams = new URLSearchParams();
    
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.equipmentName) searchParams.append('equipmentName', params.equipmentName);
    if (params.model) searchParams.append('model', params.model);
    if (params.category) searchParams.append('category', params.category);
    if (params.unit) searchParams.append('unit', params.unit);

    const queryString = searchParams.toString();
    const endpoint = `/equipment-prices${queryString ? `?${queryString}` : ''}`;

    return this.request<EquipmentPriceListResponse>(endpoint)
      .then((response) => ({
        ...response,
        data: (response.data || []).map((item) => this.normalizeEquipmentPrice(item)),
      }));
  }

  /**
   * 根据ID获取机械价格详情
   */
  async getEquipmentPriceById(id: string): Promise<ApiResponse<EquipmentPrice>> {
    return this.request<ApiResponse<EquipmentPrice>>(`/equipment-prices/${id}`)
      .then((response) => this.normalizeEquipmentPriceResponse(response));
  }

  /**
   * 创建机械价格配置
   */
  async createEquipmentPrice(data: EquipmentPriceCreateData): Promise<ApiResponse<EquipmentPrice>> {
    return this.request<ApiResponse<EquipmentPrice>>('/equipment-prices', {
      method: 'POST',
      body: JSON.stringify(data),
    }).then((response) => this.normalizeEquipmentPriceResponse(response));
  }

  /**
   * 更新机械价格配置
   */
  async updateEquipmentPrice(id: string, data: EquipmentPriceUpdateData): Promise<ApiResponse<EquipmentPrice>> {
    return this.request<ApiResponse<EquipmentPrice>>(`/equipment-prices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }).then((response) => this.normalizeEquipmentPriceResponse(response));
  }

  /**
   * 删除机械价格配置
   */
  async deleteEquipmentPrice(id: string): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/equipment-prices/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * 获取机械建议列表
   */
  async getEquipmentSuggestions(query?: string): Promise<ApiResponse<string[]>> {
    const searchParams = new URLSearchParams();
    if (query) searchParams.append('q', query);
    
    const queryString = searchParams.toString();
    const endpoint = `/equipment-prices/suggestions${queryString ? `?${queryString}` : ''}`;

    return this.request<ApiResponse<string[]>>(endpoint);
  }

  /**
   * 获取常用单位列表
   */
  async getCommonUnits(): Promise<ApiResponse<string[]>> {
    return this.request<ApiResponse<string[]>>('/equipment-prices/units');
  }

  /**
   * 获取机械分类列表
   */
  async getCategories(): Promise<ApiResponse<string[]>> {
    return this.request<ApiResponse<string[]>>('/equipment-prices/categories');
  }
}

// 创建服务实例
export const equipmentPriceService = new EquipmentPriceService();
export default equipmentPriceService;
